<template>
  <div class="feedback-tags">
    <div class="question-section">
      <h3 class="question-title">{{ questionText }}</h3>
      <span class="optional-text">{{ $t('optionalText') }}</span>
    </div>

    <div class="tags-container">
      <button
        v-for="(tag, index) in predefinedTags"
        :key="index"
        @click="toggleTag(tag)"
        :class="['tag-button', { active: selectedTags.includes(tag) }]"
        type="button"
      >
        {{ $t(`tags.${tag}`) }}
      </button>

      <button
        @click="toggleOthers"
        :class="['tag-button', 'others-button', { active: showOthersInput }]"
        type="button"
      >
        {{ $t('othersButton') }}
      </button>
    </div>

    <div v-if="showOthersInput" class="others-input-container">
      <textarea
        v-model="othersText"
        @input="updateOthersText"
        :placeholder="$t('othersPlaceholder')"
        :class="{'others-textarea': true, 'other-textarea-blank': showError}"
        rows="3"
      ></textarea>
      <div v-if="showError" class="error-message">
        {{ $t('othersRequiredError') }}
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/i18n';

export default {
  name: 'FeedbackTags',
  
  props: {
    questionText: {
      type: String,
      required: true,
    },
    predefinedTags: {
      type: Array,
      required: true,
    },
    clearTags: {
      type: Boolean,
      default: false,
    }
  },

  watch: {
    clearTags(newVal) {
      if (newVal === true) {
        this.selectedTags = [];
        this.showOthersInput = false;
        this.clearError();
      }
    }
  },
  
  emits: ['feedback-change', 'textarea'],
  
  data() {
    return {
      selectedTags: [],
      showOthersInput: false,
      othersText: '',
      showError: false,
    }
  },
  
  computed: {
    formattedFeedback() {
      const allFeedback = this.selectedTags.map(tag => i18n.global.t(`tags.${tag}`, {}, { locale: 'pt' }))

      if (this.showOthersInput && this.othersText.trim()) {
        allFeedback.push(this.othersText.trim())
      }
      
      return allFeedback.join(';')
    },
  },
  
  methods: {
    toggleTag(tag) {
      const index = this.selectedTags.indexOf(tag)
      
      if (index > -1) {
        this.selectedTags.splice(index, 1)
        this.emitFeedbackChange()
        return
      }
      this.selectedTags.push(tag)
      this.emitFeedbackChange()
    },
    
    toggleOthers() {
      this.showOthersInput = !this.showOthersInput

      this.handleSubmit()

      if (!this.showOthersInput) {
        this.clearError()
      }
      
      this.emitFeedbackChange()
    },
    
    updateOthersText() {
      this.handleSubmit()
      this.emitFeedbackChange()
    },

    clearError() {
      this.othersText = ''
      this.showError = false
      this.$emit('textarea', this.showError)
    },

    handleSubmit() {
      if (!this.othersText.trim()) {
        this.showError = true
        this.$emit('textarea', this.showError)
        return
      }
      this.showError = false
      this.$emit('textarea', this.showError)
    },
    
    emitFeedbackChange() {
      this.$emit('feedback-change', this.formattedFeedback)
    },
    
    clearSelection() {
      this.selectedTags = []
      this.showOthersInput = false
      this.othersText = ''
      this.emitFeedbackChange()
    }
  }
}
</script>

<style scoped>
.feedback-tags {
  width: 100%;
  margin-top: 1.5rem;
}

.question-section {
  margin-bottom: 1rem;
  text-align: left;
}

.question-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  display: inline;
}

.optional-text {
  font-size: 0.9rem;
  color: #666;
  font-weight: 400;
  margin-left: 0.5rem;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tag-button {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tag-button:hover {
  border-color: #dee2e6;
  background: #e9ecef;
  transform: translateY(-1px);
}

.tag-button.active {
  background: #e7f3ff;
  border-color: #0066cc;
  color: #0066cc;
}

.others-button.active {
  background: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.others-input-container {
  margin-bottom: 1rem;
  animation: slideDown 0.3s ease;
}

.error-message {
  color: red;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.others-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease;
}

.other-textarea-blank {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease;
  outline: none;
  border-color: red;
}

.others-textarea::placeholder {
  color: #6c757d;
}

@media (max-width: 768px) {
  .tag-button {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
  
  .others-textarea {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .tags-container {
    gap: 0.4rem;
  }
  
  .tag-button {
    font-size: 0.75rem;
    padding: 0.35rem 0.7rem;
  }
}
</style>